'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { toast } from '@/components/ui/use-toast';
import { logError } from '@/utils/logger';
import {
  getGroupDependentLabels,
  getKnockoutPlaceholderLabels,
  getSharedGrid,
  saveGridMoves,
} from '@/api/tournament/tournamentApi';
import { getRounds } from '@/api/tournament/tournamentRoundApi';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { exportGridPdf } from '@/components/tournament/grid/grid-pdf';
import { SharedGridView } from '@/components/tournament/grid/SharedGridView';
import { GridPendingChangesBar } from '@/components/tournament/grid/GridPendingChangesBar';
import { GridEditMatchModal } from '@/components/tournament/grid/GridEditMatchModal';
import { GridAvailableTeamsDialog } from '@/components/tournament/grid/GridAvailableTeamsDialog';
import {
  gridForWeek,
  initialWeekStart,
  tournamentsWithUnresolvedTeams,
  type GridSlotRef,
  scheduleAvailabilityFor,
  toPendingMove,
  weeksFromRounds,
  type GridWeek,
  type PlaceholderLabelMap,
} from '@/components/tournament/grid/grid-model';
import { TournamentFormat, type GridMatch, type GridMove, type SharedGrid } from '@/types/tournament.model';

/** Only the round fields the week selector uses (the full list builds the matches and availability). */
const ROUND_WEEK_FIELDS = {
  id: true,
  roundNumber: true,
  status: true,
  weekStartDate: true,
  weekEndDate: true,
} as const;

/** Remounts the page per tournament so no state (weeks, grid, pending moves) leaks between them. */
export default function TournamentGridPage() {
  const { id = '' } = useParams<{ id: string }>();
  return <TournamentGridPageContent key={id} />;
}

function TournamentGridPageContent() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [grid, setGrid] = useState<SharedGrid | null>(null);
  const [activeClubId, setActiveClubId] = useState('');
  // Empty for tournaments without rounds (SHORT / AMERICAN): then there's no week selector.
  const [weeks, setWeeks] = useState<GridWeek[]>([]);
  const [activeWeekStart, setActiveWeekStart] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // matchId -> `YYYY-MM-DDTHH:mm:ss` local, not yet saved. Keyed by match on purpose: moving
  // the same match twice overwrites its pending move instead of stacking two of them.
  const [moves, setMoves] = useState<Map<string, string>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState<GridMatch | null>(null);
  // The slot whose "who can play" list is open. Unlike the edit modal it only reads, so it can
  // open with a draft pending.
  const [availableTeamsSlot, setAvailableTeamsSlot] = useState<GridSlotRef | null>(null);

  // matchId -> "2° Grupo B" per undecided bracket slot. Kept out of `grid` because it comes from a
  // different endpoint and is allowed to be missing: the grid renders fine without it.
  const [placeholderLabels, setPlaceholderLabels] = useState<PlaceholderLabelMap>(
    new Map(),
  );
  const [isExporting, setIsExporting] = useState(false);
  // Bumped by every `loadPlaceholderLabels` run, so only the latest one gets to write its labels.
  const labelsRequestRef = useRef(0);

  /**
   * Fills in the labels for slots whose team isn't decided yet: the qualifier on a bracket slot
   * ("2° Grupo B"), and the source match on a group of 4's dependent match ("Ganador Partido 1").
   *
   * Deliberately fire-and-forget, and deliberately never surfaced as an error: these are a nicety
   * on top of a grid that is already complete and usable, so a failure here must not block the
   * page or alarm the organizer — the cards just keep saying "Equipo 1"/"Equipo 2".
   *
   * One request per fixture that can label something: the groups of a tournament with an
   * undecided group match, the bracket of one with an undecided bracket match. The grid shows
   * sibling tournaments too, so all of them run in parallel, and each fails on its own: a broken
   * groups call doesn't take the bracket labels with it. Nothing is requested once every team in
   * the grid is resolved — the common case after the group stage.
   *
   * Runs can overlap (the initial load and an edit modal's refresh both fetch the grid), and the
   * slowest fixture request decides when each one ends. A run that finishes after a newer one
   * started is dropped, or labels built from the older grid would overwrite the current ones.
   */
  const loadPlaceholderLabels = useCallback(async (data: SharedGrid) => {
    const request = ++labelsRequestRef.current;
    const unresolved = tournamentsWithUnresolvedTeams(data);
    const orEmpty = (err: unknown): PlaceholderLabelMap => {
      logError('TournamentGridPage.loadPlaceholderLabels', err);
      return new Map();
    };
    const perFixture = await Promise.all([
      ...[...unresolved.knockout].map((id) => getKnockoutPlaceholderLabels(id).catch(orEmpty)),
      ...[...unresolved.groups].map((id) => getGroupDependentLabels(id).catch(orEmpty)),
    ]);
    if (request !== labelsRequestRef.current) return;
    setPlaceholderLabels(new Map(perFixture.flatMap((m) => [...m])));
  }, []);

  // Shared by the initial load and by the fine-grained edit modal's post-save refresh, so both
  // paths apply the same club-selection logic below instead of drifting apart. Doesn't touch
  // `loading` itself: the initial-load effect below owns that, since a full-page "Cargando..."
  // flash on every single-match edit (which already has its own "Guardando..." button state)
  // would be jarring. Returns whether it succeeded: the modal's post-save refresh needs to know,
  // since a PATCH that saved fine but a refetch that then fails would otherwise show a success
  // toast while the grid keeps rendering the match at its old cell.
  const fetchGrid = useCallback(async (): Promise<boolean> => {
    try {
      const data = await getSharedGrid(id);
      setGrid(data);
      // Keep the currently active club if it's still in the refreshed grid; only fall back to
      // the first one when there wasn't an active club yet (the initial load).
      setActiveClubId((prev) =>
        prev && data.clubs.some((c) => c.id === prev) ? prev : (data.clubs[0]?.id ?? ''),
      );
      setError(null);
      void loadPlaceholderLabels(data);
      return true;
    } catch (err) {
      // Redacted on purpose: an axios error carries `config.headers`, and the request
      // interceptor puts the bearer token there (see .claude/rules/security.md).
      logError('TournamentGridPage.fetchGrid', err);
      setError('No se pudo cargar la grilla.');
      return false;
    }
  }, [id, loadPlaceholderLabels]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchGrid().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchGrid]);

  // A LONG tournament's grid spans every week of the league: it's shown one week at a time. A failure
  // here only costs the selector, so it doesn't surface as a page error.
  // Rounds only exist for LONG tournaments. A grid that doesn't say its tournaments' format is
  // treated as possibly LONG, so the selector isn't lost.
  const hasLongTournament =
    grid?.tournaments.some((t) => t.format == null || t.format === TournamentFormat.LONG) ?? false;
  useEffect(() => {
    if (!hasLongTournament) return;
    let cancelled = false;
    getRounds(id, ROUND_WEEK_FIELDS)
      .then((rounds) => {
        if (cancelled) return;
        setWeeks(weeksFromRounds(rounds));
        setActiveWeekStart((prev) => prev ?? initialWeekStart(rounds));
      })
      .catch((err) => logError('TournamentGridPage.loadRounds', err));
    return () => {
      cancelled = true;
    };
  }, [id, hasLongTournament]);

  const activeWeek = weeks.find((w) => w.start === activeWeekStart) ?? null;
  const visibleGrid = useMemo(
    () => (grid && activeWeek ? gridForWeek(grid, activeWeek) : grid),
    [grid, activeWeek],
  );

  // Warns on tab close / refresh while there's an unsaved draft. Leaving via the "← Volver al
  // torneo" button goes through its own confirmation dialog instead (see `handleBack`), since
  // `beforeunload` can't be intercepted with a custom dialog.
  useEffect(() => {
    if (moves.size === 0) return;
    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [moves.size]);

  const tournamentScheduleAvailability = useMemo(
    () => (visibleGrid ? scheduleAvailabilityFor(visibleGrid, activeClubId) : []),
    [visibleGrid, activeClubId],
  );

  const handleEditMatch = (match: GridMatch) => setEditingMatch(match);

  const handleMove = (matchId: string, date: string, time: string) => {
    setMoves((prev) => {
      const next = new Map(prev);
      next.set(matchId, toPendingMove(date, time));
      return next;
    });
  };

  const handleDiscard = () => setMoves(new Map());

  const handleSave = async () => {
    if (!grid || moves.size === 0) return;

    // Built by walking `grid.matches` and keeping the ones with a pending move, instead of
    // walking `moves` and looking each match up: this way `tournamentId` always comes straight
    // from the match that owns it and can never fall back to an empty string. It also lets us
    // detect a stale `matchId` (one no longer present in `grid.matches`) via the length check
    // below, instead of silently sending the backend a malformed move for it.
    const payload: GridMove[] = grid.matches
      .filter((m) => moves.has(m.matchId))
      .map((m) => ({
        matchId: m.matchId,
        tournamentId: m.tournamentId,
        startTime: moves.get(m.matchId)!,
      }));

    if (payload.length !== moves.size) {
      toast({
        title: 'No se pudieron guardar los cambios',
        description: 'La grilla cambió mientras editabas. Recargá la página e intentá de nuevo.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      // The POST returns the recalculated grid instead of just acknowledging the save: without
      // that, a separate GET would be needed to see the new overlap flags, leaving a window
      // where the screen still shows the stale grid right after a save that just changed it.
      const updated = await saveGridMoves(id, payload);
      setGrid(updated);
      setMoves(new Map());
      toast({ title: 'Horarios guardados' });
    } catch (err) {
      // `moves` is deliberately left untouched on failure: the backend rejects the whole batch
      // when any move leaves a team with two matches at the same time, and names the conflicting
      // matches in the message below. Clearing the draft here would force the organizer to redo
      // every move instead of just fixing the one that conflicts.
      toast({
        title: 'No se pudieron guardar los cambios',
        description:
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Probá de nuevo en unos segundos.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPdf = async () => {
    // The PDF is the week the organizer is looking at.
    if (!visibleGrid) return;
    setIsExporting(true);
    try {
      await exportGridPdf(visibleGrid, moves, placeholderLabels);
    } catch (err) {
      logError('TournamentGridPage.exportGridPdf', err);
      toast({
        title: 'No se pudo generar el PDF',
        description: 'Probá de nuevo en unos segundos.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleBack = () => {
    if (moves.size > 0) {
      setConfirmLeaveOpen(true);
      return;
    }
    navigate(`/torneos/${id}`);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al torneo
        </Button>
        <h1 className="text-lg font-semibold">Grilla compartida</h1>

        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={handleExportPdf}
          // Disabled with a pending draft on purpose: printing an unsaved draft means walking
          // out with paper that doesn't match what the backend has, and paper can't be undone.
          disabled={
            !visibleGrid || visibleGrid.matches.length === 0 || moves.size > 0 || isExporting || loading
          }
          title={
            moves.size > 0
              ? 'Guardá o descartá los cambios antes de descargar la planilla.'
              : undefined
          }
        >
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? 'Generando...' : 'Descargar PDF'}
        </Button>
      </div>

      {weeks.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="grid-week">Semana</Label>
          <Select
            value={activeWeekStart ?? undefined}
            onValueChange={setActiveWeekStart}
            disabled={moves.size > 0}
          >
            <SelectTrigger
              id="grid-week"
              className="w-80"
              title={
                moves.size > 0
                  ? 'Guardá o descartá los cambios antes de cambiar de semana.'
                  : undefined
              }
            >
              <SelectValue placeholder="Elegí una semana" />
            </SelectTrigger>
            <SelectContent>
              {weeks.map((week) => (
                <SelectItem key={week.start} value={week.start}>
                  {week.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Para pasar un partido a otra semana usá Reprogramar desde la jornada.
          </p>
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Cargando la grilla...</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {visibleGrid && !loading && (
        <SharedGridView
          grid={visibleGrid}
          activeClubId={activeClubId}
          onActiveClubIdChange={setActiveClubId}
          moves={moves}
          onEditMatch={handleEditMatch}
          placeholderLabels={placeholderLabels}
          onMove={handleMove}
          onShowAvailableTeams={setAvailableTeamsSlot}
        />
      )}

      <GridPendingChangesBar
        count={moves.size}
        isSaving={isSaving}
        onDiscard={handleDiscard}
        onSave={handleSave}
      />

      <GridEditMatchModal
        match={editingMatch}
        onOpenChange={(open) => {
          if (!open) setEditingMatch(null);
        }}
        tournamentScheduleAvailability={tournamentScheduleAvailability}
        onSaved={fetchGrid}
      />

      <GridAvailableTeamsDialog
        tournamentId={id}
        slot={availableTeamsSlot}
        onOpenChange={(open) => {
          if (!open) setAvailableTeamsSlot(null);
        }}
      />

      <ConfirmationModal
        isOpen={confirmLeaveOpen}
        onClose={() => setConfirmLeaveOpen(false)}
        onConfirm={() => navigate(`/torneos/${id}`)}
        title="¿Salir sin guardar?"
        description="Tenés cambios sin guardar en la grilla. Si salís ahora, se pierden."
        confirmText="Salir sin guardar"
        cancelText="Seguir editando"
      />
    </div>
  );
}
